import { toast } from 'sonner';
import { useMutation } from '@apollo/client/react';

import { ToggleGroupCard, ToggleGroupCardItem } from '@/components/ui/toggle-group';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { AgreementUpdateDocument } from 'src/graphql/actions/agreement/agreementUpdate.mutation';
import { ClientError, getApiError } from 'src/utils/errors';

import { agreementTemplateOptionsRich } from '../model';

type Props = {
  escrowId: string;
  isThirdPartyAgreement: boolean;
};

const AgreementTemplateSettings = ({ escrowId, isThirdPartyAgreement }: Props) => {
  const [updateAgreement] = useMutation(AgreementUpdateDocument);

  const onUpdateAgreement = async (thirdPartyAgreement: string) => {
    if (thirdPartyAgreement === '') return;

    try {
      const { data } = await updateAgreement({
        variables: {
          escrowId,
          agreementParams: {
            thirdPartyAgreement: thirdPartyAgreement === 'true',
          },
        },
      });

      if (data?.agreementUpdate?.success) {
        toast.success('Saved!');
      } else {
        throw new ClientError(data?.agreementUpdate?.errors?.[0]);
      }
    } catch (error: unknown) {
      getApiError(error, toast.error);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Agreement template</CardTitle>
        <CardDescription>
          Choose between our standard Codekeeper template, tailored to your needs, or send us your own legal terms to
          use as the basis for your escrow agreement.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ToggleGroupCard
          type="single"
          className="flex-col w-full"
          onValueChange={onUpdateAgreement}
          value={isThirdPartyAgreement ? 'true' : 'false'}
        >
          {agreementTemplateOptionsRich.map(({ value, icon: Icon, label, description }) => (
            <ToggleGroupCardItem key={value} value={value} icon={Icon} title={label} description={description} />
          ))}
        </ToggleGroupCard>
      </CardContent>
    </Card>
  );
};

export default AgreementTemplateSettings;
